import { Check, Sparkles, Crown } from "lucide-react";
import EditableText from "./EditableText";
import { useText } from "@/lib/ContentContext";

export const PricingStrip = () => {
  const bullets = [
    { k: "pricing.bullet.0", d: "Locked-in founding rate, forever" },
    { k: "pricing.bullet.1", d: "First access on August 1, 2026" },
    { k: "pricing.bullet.2", d: "All features — no tier gates" },
    { k: "pricing.bullet.3", d: "Early-access community circle" },
  ];

  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="relative py-24 sm:py-32 px-6 sm:px-10 bg-[#F4ECFF]"
    >
      <div className="max-w-6xl mx-auto">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <EditableText k="pricing.eyebrow" fallback="The founding offer" className="eyebrow" data-testid="pricing-eyebrow" />
          <h2 className="editorial-display mt-5 text-4xl sm:text-5xl lg:text-6xl text-[#0B0A4A]" data-testid="pricing-heading">
            <EditableText k="pricing.heading" fallback="Pay half the price — or less — forever." multiline />
          </h2>
          <p className="mt-5 text-base sm:text-lg text-[#0B0A4A]/75 font-sans" data-testid="pricing-subheading">
            <EditableText
              k="pricing.subheading"
              fallback="Everyone on the waitlist gets $13/month for life — or just $10/month when billed annually — instead of the launch price of $25. One rate. No raises. No catch."
              multiline
            />
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-5 lg:gap-6">
          {/* Regular price */}
          <div className="relative rounded-3xl border border-[#E0D0F5] bg-white/60 backdrop-blur-sm p-8 sm:p-10" data-testid="pricing-regular-card">
            <EditableText k="pricing.regular.label" fallback="Regular launch price" className="eyebrow text-[#0B0A4A]/50" />
            <div className="mt-5 flex items-baseline gap-2">
              <span className="font-serif text-6xl sm:text-7xl text-[#0B0A4A]/60 line-through decoration-[#3E1A7A]/70 decoration-2">
                <EditableText k="pricing.regular.amount" fallback="$25" />
              </span>
              <EditableText k="pricing.regular.period" fallback="/month" className="text-base text-[#0B0A4A]/60" />
            </div>
            <p className="mt-6 text-sm text-[#0B0A4A]/65 font-sans leading-relaxed">
              <EditableText
                k="pricing.regular.note"
                fallback="What everyone else will pay after launch. Same app, same AI — almost double the waitlist rate."
                multiline
              />
            </p>
          </div>

          {/* Founder price */}
          <div className="relative rounded-3xl bg-[#0B0A4A] text-[#F4ECFF] p-8 sm:p-10 overflow-hidden shadow-[0_30px_80px_-30px_rgba(11,10,74,0.55)]" data-testid="pricing-founder-card">
            <div
              className="absolute -top-24 -right-24 w-72 h-72 rounded-full opacity-30 blur-3xl pointer-events-none"
              style={{ background: "radial-gradient(circle, #B87CFF 0%, transparent 70%)" }}
            />

            <div className="flex items-center justify-between relative gap-2">
              <EditableText k="pricing.founder.eyebrow" fallback="Founding rate · Waitlist only" className="eyebrow text-[#EADDFF]" />
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] bg-[#B87CFF] text-[#0B0A4A] px-2.5 py-1 rounded-full">
                <Sparkles className="w-3 h-3" />
                <EditableText k="pricing.founder.badge" fallback="Best value" />
              </span>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-3 relative" data-testid="pricing-billing-options">
              <div className="rounded-2xl bg-[#F4ECFF]/5 border border-[#F4ECFF]/15 p-4" data-testid="pricing-monthly-option">
                <EditableText k="pricing.monthly.label" fallback="Monthly" as="p" className="text-[10px] uppercase tracking-[0.18em] text-[#F4ECFF]/55" />
                <div className="mt-2 flex items-baseline gap-1">
                  <span className="font-serif text-4xl sm:text-5xl text-[#F4ECFF]">
                    <EditableText k="pricing.monthly.amount" fallback="$13" />
                  </span>
                  <EditableText k="pricing.monthly.period" fallback="/mo" className="text-xs text-[#F4ECFF]/70" />
                </div>
                <EditableText k="pricing.monthly.note" fallback="Billed monthly · for life" as="p" className="mt-1.5 text-[11px] text-[#F4ECFF]/55" />
              </div>
              <div className="rounded-2xl bg-[#B87CFF]/15 border border-[#B87CFF]/40 p-4 relative" data-testid="pricing-annual-option">
                <div className="absolute -top-2 right-3 inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.2em] bg-[#B87CFF] text-[#0B0A4A] px-2 py-0.5 rounded-full">
                  <Crown className="w-2.5 h-2.5" />
                  <EditableText k="pricing.annual.badge" fallback="Save more" />
                </div>
                <EditableText k="pricing.annual.label" fallback="Annual" as="p" className="text-[10px] uppercase tracking-[0.18em] text-[#F4ECFF]/55" />
                <div className="mt-2 flex items-baseline gap-1">
                  <span className="font-serif text-4xl sm:text-5xl text-[#F4ECFF]">
                    <EditableText k="pricing.annual.amount" fallback="$10" />
                  </span>
                  <EditableText k="pricing.annual.period" fallback="/mo" className="text-xs text-[#F4ECFF]/70" />
                </div>
                <EditableText k="pricing.annual.note" fallback="$120 once · saves $180/yr" as="p" className="mt-1.5 text-[11px] text-[#F4ECFF]/55" />
              </div>
            </div>

            <ul className="mt-7 space-y-3 relative" data-testid="pricing-included-list">
              {bullets.map((b) => (
                <li key={b.k} className="flex items-start gap-3 text-sm text-[#F4ECFF]/90">
                  <span className="mt-0.5 inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#B87CFF] flex-shrink-0">
                    <Check className="w-3 h-3 text-[#0B0A4A]" strokeWidth={3} />
                  </span>
                  <EditableText k={b.k} fallback={b.d} />
                </li>
              ))}
            </ul>

            <a
              href="#waitlist"
              className="mt-8 relative inline-flex items-center justify-center w-full rounded-full bg-[#3E1A7A] hover:bg-[#B87CFF] hover:text-[#0B0A4A] text-white px-6 py-3 text-sm font-medium tracking-wide transition-colors duration-300"
              data-testid="pricing-cta-btn"
            >
              <EditableText k="pricing.cta" fallback="Lock in founding rate" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingStrip;
